import { ErrorMessages, MessageField, SignUpSource, UserType } from '@/constants';
import type { GaEventBaseParams, LoginMethod } from '@/interfaces';
import type { OriginalUserInfo } from '@/utils/umsDataModify.utils';
import type { CodeResponse } from '@react-oauth/google';
import { umsFetcher } from './base.fetchers';

type LogoutResponse = {
  data: string;
  status_code: number;
  message: string;
};

type LogoutParams = {
  refreshToken: string;
} & GaEventBaseParams;

export const logout = async ({
  refreshToken,
  clientId,
  sessionId,
  eventTimestamp,
}: LogoutParams): Promise<LogoutResponse | undefined> => {
  try {
    const payload = {
      refresh_token: refreshToken,
      ga_event: {
        client_id: clientId,
        session_id: sessionId,
        event_timestamp: eventTimestamp,
      },
    };

    const res = await umsFetcher.post('v1/auth/logout', payload);
    const response: LogoutResponse = await res.json();

    if (response?.status_code !== 200) {
      throw new Error(`Failed to logout: ${response?.message}`);
    }

    return response;
  } catch (error) {
    console.error('/api/v1/auth/logout', error);
    return undefined;
  }
};

export type AuthenticatePMPayload = {
  mediumOrigin: 'google' | 'linkedin';
  userType?: UserType;
  code: string;
  redirectUri?: string;
  country?: string;
  signUpSource: SignUpSource;
  newsletterOptIn: boolean;
  gaClientId?: string;
  gaSessionId?: string;
  eventTimestamp?: string;
  cta?: string;
  authMethod?: LoginMethod;
};

export const authenticatePMFetcher = async ({
  mediumOrigin,
  userType = UserType.Professional,
  code,
  redirectUri,
  country,
  signUpSource,
  newsletterOptIn,
  gaClientId,
  gaSessionId,
  eventTimestamp,
  cta,
  authMethod,
}: AuthenticatePMPayload): Promise<TAuthResponse> => {
  const payload = {
    medium_origin: mediumOrigin,
    user_type: userType,
    code,
    redirect_uri: redirectUri,
    country,
    sign_up_source: signUpSource,
    newsletter_opt_in: newsletterOptIn,
    ga_event: {
      client_id: gaClientId,
      session_id: gaSessionId,
      event_timestamp: eventTimestamp,
      cta,
      authentication_method: authMethod,
    },
  };

  try {
    const res = await umsFetcher.post('authenticate', payload, { skipAuth: true });

    const data = await res.json();

    const isTokenValid = !!data.access_token;

    if (!isTokenValid) {
      throw ErrorMessages[MessageField.AUTHENTICATE_ERROR];
    }

    return data;
  } catch (error) {
    console.error('authenticate', error);
    throw ErrorMessages[MessageField.AUTHENTICATE_ERROR];
  }
};

export type AuthenticateEMSPayload = {
  code: CodeResponse['code'];
  signUpSource: SignUpSource;
  newsletterOptIn: boolean;
  scope: string;
  country?: string;
  userId?: string;
  redirectUri?: string;
  gaClientId?: string;
  gaSessionId?: string;
  eventTimestamp?: string;
  cta?: string;
  authMethod?: LoginMethod;
  utmParams?: Record<string, string>;
};

export type TAuthResponse = {
  access_token: string;
  refresh_token: string;
  expires_in: number;
  user_info: OriginalUserInfo;
  token_type: string;
  created?: boolean;
  ems_first_grant?: boolean;
};

export const authenticateEMSFetcher = async ({
  code,
  signUpSource = SignUpSource.ConsumerWeb,
  newsletterOptIn = false,
  scope,
  country,
  userId,
  redirectUri,
  gaClientId,
  gaSessionId,
  eventTimestamp,
  utmParams,
  cta,
  authMethod,
}: AuthenticateEMSPayload): Promise<TAuthResponse> => {
  try {
    const response = await umsFetcher.post(
      'emailsync/auth',
      {
        code,
        sign_up_source: signUpSource,
        newsletter_opt_in: newsletterOptIn,
        scope, // TODO: recheck if we need to pass this
        country,
        user_id: userId,
        redirect_uri: redirectUri,
        ga_event: {
          client_id: gaClientId,
          session_id: gaSessionId,
          event_timestamp: eventTimestamp,
          cta,
          authentication_method: authMethod,
          ...utmParams,
        },
      },
      { skipAuth: true },
    );

    if (response.ok) {
      const data = await response.json();
      return data;
    }

    const errorText = await response.text();
    throw JSON.parse(errorText);
  } catch (error) {
    console.error(error);
    throw error;
  }
};

export type AuthenticateEmailPayload = {
  email: string;
  browserUuid?: string;
  country?: string;
  signUpSource?: SignUpSource;
  newsletterOptIn: boolean;
  gaClientId?: string;
  gaSessionId?: string;
  eventTimestamp?: string;
  cta?: string;
  authMethod?: LoginMethod;
};

export const authenticateEmailFetcher = async ({
  email,
  browserUuid,
  country,
  signUpSource,
  newsletterOptIn,
  gaClientId,
  gaSessionId,
  eventTimestamp,
  cta,
  authMethod,
}: AuthenticateEmailPayload): Promise<TAuthResponse> => {
  const payload = {
    email,
    browser_uuid: browserUuid || null,
    country,
    sign_up_source: signUpSource || null,
    newsletter_opt_in: newsletterOptIn,
    ga_event: {
      client_id: gaClientId,
      session_id: gaSessionId,
      event_timestamp: eventTimestamp,
      cta,
      authentication_method: authMethod,
    },
  };

  try {
    const res = await umsFetcher.post('email-authenticate', payload, { skipAuth: true });

    const data = await res.json();
    const dataWithStatus = { ...data, status: res.status };

    return dataWithStatus;
  } catch (err) {
    console.error('email-authenticate ', err);
    throw new Error(ErrorMessages[MessageField.AUTHENTICATE_ERROR].error);
  }
};

type GuestLoginResponse = {
  user_info: {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    profile_picture_url: string;
    user_type: string;
    country: string;
    company_name: string;
    authentication_method: string;
    opt_in_date: string;
    default_opt_in: boolean;
    newsletter_opt_in: boolean;
    no_tracked_ids: number;
    email_sync_opt_in: boolean;
    subscribe_to_email_summary: boolean;
    push_notification_opt_in: boolean;
  };
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
};

/**
 * Generate a new guest user and log them in
 * @returns Promise with guest user info and tokens
 */
export const guestLoginFetcher = async ({ country }: { country: string }): Promise<GuestLoginResponse> => {
  try {
    const res = await umsFetcher.post('v1/auth/guest-login', { country });

    const response: GuestLoginResponse = await res.json();

    if (!response?.user_info?.id || !response?.access_token) {
      throw new Error('Failed to create guest user: Invalid response');
    }

    return response;
  } catch (error) {
    console.error('/api/v1/auth/guest-login ', error);
    throw error;
  }
};
